/**
 * route.ts — URL ⇄ app state for the signed-in shell.
 *
 * Deliberately small: views are still React state in App.tsx, this only maps
 * them to paths so links can be shared, reload keeps your place and the
 * browser's Back/Forward work. `/meet/:uuid` and `/guest/:token` belong to the
 * guest flow in main.tsx and are never produced here.
 */
import type { View } from '../App';

export interface Route {
  view: View;
  /** Open chat room, only for the chat view */
  roomId?: string;
  /** Phones: this entry opened the room from the room list, so Back returns to the list */
  fromList?: boolean;
}

const VIEWS: readonly View[] = [
  'today', 'chat', 'mail', 'teams', 'meet', 'files', 'calendar', 'workflows', 'settings', 'compliance',
];

/** Alternative paths that land on a view; /activity was Today's old name */
const ALIASES: Record<string, View> = { activity: 'today' };

const ROOM_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function parsePath(pathname: string): Route | null {
  let parts: string[];
  try { parts = pathname.split('/').filter(Boolean).map(decodeURIComponent); } catch { return null; }
  if (parts.length === 0) return null;
  const head = parts[0]!.toLowerCase();
  const view = (VIEWS as readonly string[]).includes(head) ? head as View : ALIASES[head];
  if (!view) return null;
  if (view === 'chat' && parts[1] && ROOM_ID.test(parts[1])) return { view, roomId: parts[1] };
  if (parts.length > 1) return view === 'meet' ? null : { view };
  return { view };
}

export function pathFor(route: Route): string {
  if (route.view === 'chat' && route.roomId) return `/chat/${encodeURIComponent(route.roomId)}`;
  return `/${route.view}`;
}

/** Push a new history entry unless the URL already matches. */
export function pushRoute(route: Route) {
  const path = pathFor(route);
  if (window.location.pathname !== path) window.history.pushState(route, '', path);
}

/**
 * Rewrite the current entry. Keeps the query string and hash: this runs on startup,
 * before the login page has read an SSO return (#access_token=…&refresh_token=…),
 * and dropping them signs the user straight back out.
 */
export function replaceRoute(route: Route) {
  const path = pathFor(route);
  if (window.location.pathname !== path) {
    window.history.replaceState(route, '', path + window.location.search + window.location.hash);
  }
}
